import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Job, JobExecutionStatus } from '@aws-sdk/client-iot';
import { AuditAction, AuditType } from '@common/audit-log/models/AuditLog';
import { lastValueFrom, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SimpleThingProperties } from '../api/backend/services/things/thing-properties';
import { ThingsService } from '../api/backend/services/things/things.service';
import { JobsService } from '../api/jobs.service';
import {
  GetExecutionsResponse,
  JobExecutionSummaryForJobAndStatistics,
} from '../models/backend/deployment/get-executions';
import { DeploymentThing } from '../models/deployment';
import {
  FirmwareDistributionChartDataByType,
  FirmwareDistributionTotals,
} from '../models/firmware-distribution';
import { GroupOfThings } from '../models/Group-of-things.model';
import { MetaVersionJob } from '../models/meta-version-job.model';
import { NotificationService } from '../shared/notification.service';
import { Utils } from '../shared/utils';
import { ArnUtils } from '../shared/utils/arn.utils';
import { RolloutUtils } from '../shared/utils/rollout.utils';
import { ThingUtils } from '../utils/thing.utils';

type JobExecutionStatusAll = JobExecutionStatus | 'ALL';

@Component({
  selector: 'app-deployment',
  templateUrl: './deployment.component.html',
  styleUrls: ['./deployment.component.scss'],
})
export class DeploymentComponent implements OnInit {
  job?: Job;
  metaversionJob?: MetaVersionJob;
  jobId?: string | null;
  jobDocument?: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  parsedDocument: any;
  devices?: Array<DeploymentThing>;
  sumAll = 0;
  sumDone = 0;
  sumQueued = 0;
  sumInProgress = 0;
  sumSuccess = 0;
  sumFail = 0;
  sumIgnored = 0;
  statusFilter: JobExecutionStatusAll = 'ALL';
  devicesStatus: string[] = [];
  successProgress?: number;
  failProgress?: number;
  ignoredProgress?: number;
  statusFilters: JobExecutionStatusAll[] = ['ALL'];
  statusFilterValue = this.statusFilters[0];
  showDocument = false;

  nextToken?: string; // for normal jobs (not split) will be used to get the next devices in the job execution
  isJobSplit = false; // true if the job is split into several jobs ("related jobs")
  relatedJobIds: string[] = []; // in case of split job, will iterate over related jobs instead of using nextToken

  firmwareDistributionTotals?: FirmwareDistributionTotals;
  firmwareDistribution?: FirmwareDistributionChartDataByType;

  item: unknown[] = [];

  group$?: Observable<GroupOfThings | string | null>;

  // Devices "ramp-up"-ed not queued yet
  remainingDevicesToQueue: number | null = null;

  readonly AuditAction = AuditAction;
  readonly AuditType = AuditType;

  constructor(
    private activatedRoute: ActivatedRoute,
    private notif: NotificationService,
    private jobService: JobsService,
    private thingsService: ThingsService,
  ) {}

  ngOnInit(): void {
    // getting params jobId
    this.activatedRoute.paramMap.subscribe(async (params) => {
      this.jobId = params.get('jobId');
      // getting the job data
      this.refresh().catch((err: Error) =>
        this.notif.showError(err.message, err),
      );
    });
  }

  async refreshJob(): Promise<void> {
    try {
      await this.refresh();
      this.notif.showSuccess('Job refreshed');
    } catch (e) {
      this.notif.showError((e as Error).message, e);
    }
  }

  async cancelJob(): Promise<void> {
    try {
      await this.jobService.cancelJob(this.job?.jobId || '');

      this.notif.showSuccess('Deployment canceled!');
    } catch (e) {
      this.notif.showError((e as Error).message, e);
    }
  }

  async getNextDevices(): Promise<void> {
    if (!this.jobId) {
      return;
    }

    const devicesForJob = await this.listDevices(this.jobId);

    const newDevices = devicesForJob.executionSummaries || [];
    const nextToken = devicesForJob.nextToken;

    await this.setDevices(
      this.devices ? this.devices.concat(newDevices) : newDevices,
    );
    this.nextToken = nextToken;
  }

  listDevices(jobId: string): Promise<GetExecutionsResponse> {
    const statusFilter =
      this.statusFilterValue === 'ALL' ? undefined : this.statusFilterValue;

    if (this.isJobSplit) {
      const relatedJobId = this.relatedJobIds.shift()!;
      return this.jobService.listDevicesForJob(relatedJobId, statusFilter);
    } else {
      return this.jobService.listDevicesForJob(
        jobId,
        statusFilter,
        this.nextToken,
      );
    }
  }

  splitSerial(thingArn: string): string {
    return ArnUtils.getResourceId(thingArn) ?? '';
  }

  async onChange(e: Event): Promise<void> {
    if (this.isJobExecutionStatusAll((e.target as HTMLOptionElement).value)) {
      this.statusFilterValue = (e.target as HTMLOptionElement)
        .value as JobExecutionStatusAll;
    }
    this.devices = [];
    this.nextToken = undefined;
    await this.getNextDevices();
  }

  private async refresh(): Promise<void> {
    try {
      if (!this.jobId) {
        return;
      }

      const [
        job,
        metaversionJob,
        jobDocument,
        devicesForJob,
        firmwareDistribution,
      ] = await Promise.all([
        this.jobService.getJob(this.jobId),
        this.jobService.getMetaversionForJob(this.jobId),
        this.jobService.getJobDocument(this.jobId),
        this.jobService.listDevicesForJob(this.jobId),
        this.jobService
          .getFirmwareDistribution(this.jobId)
          .catch(() => undefined),
      ]);

      if (firmwareDistribution) {
        this.firmwareDistributionTotals = {
          ui: firmwareDistribution.uiDistribution.length,
          wifi: firmwareDistribution.wifiDistribution.length,
        };
        this.firmwareDistribution = {
          ui: {
            labels: firmwareDistribution.uiDistribution.map(
              (distribution) => distribution.keyValue,
            ),
            datasets: [
              {
                label: 'count',
                data: firmwareDistribution.uiDistribution.map(
                  (distribution) => distribution.count,
                ),
              },
            ],
          },
          wifi: {
            labels: firmwareDistribution.wifiDistribution.map(
              (distribution) => distribution.keyValue,
            ),
            datasets: [
              {
                label: 'count',
                data: firmwareDistribution.wifiDistribution.map(
                  (distribution) => distribution.count,
                ),
              },
            ],
          },
        };
      }

      // Default status
      const statusFilter = 'ALL';

      // Preparing sums
      let sumAll = 0;
      let sumDone = 0;
      let sumQueued = 0;
      let sumInProgress = 0;
      let sumSuccess = 0;
      let sumFail = 0;
      let sumIgnored = 0;

      const devicesStatus: JobExecutionStatus[] =
        Object.values(JobExecutionStatus);
      const devices: JobExecutionSummaryForJobAndStatistics[] =
        devicesForJob.executionSummaries || [];
      const nextToken = devicesForJob.nextToken;

      const jobProcessDetails = job.jobProcessDetails;
      if (jobProcessDetails) {
        sumQueued = jobProcessDetails.numberOfQueuedThings || 0;
        sumInProgress = jobProcessDetails.numberOfInProgressThings || 0;
        sumSuccess = jobProcessDetails.numberOfSucceededThings || 0;
        sumFail =
          (jobProcessDetails.numberOfFailedThings || 0) +
          (jobProcessDetails.numberOfTimedOutThings || 0);
        sumIgnored =
          (jobProcessDetails.numberOfRejectedThings || 0) +
          (jobProcessDetails.numberOfRemovedThings || 0) +
          (jobProcessDetails.numberOfCanceledThings || 0);
        sumDone = sumSuccess + sumFail + sumIgnored;
        sumAll = sumQueued + sumInProgress + sumDone;
      }

      // Refresh UI
      this.job = job;
      this.metaversionJob = metaversionJob;
      if (metaversionJob) {
        const relatedJobs = metaversionJob.relatedJobs ?? [];
        this.isJobSplit = relatedJobs.length > 0; // related jobs = job is split into several jobs
        this.relatedJobIds = relatedJobs;
      }

      this.jobDocument = jobDocument;
      try {
        this.parsedDocument = JSON.parse(jobDocument);
      } catch (e) {
        this.notif.showError('Job document not formated correctly', e);
        this.parsedDocument = jobDocument;
      }
      this.sumAll = sumAll;
      this.sumDone = sumDone;
      this.sumQueued = sumQueued;
      this.sumInProgress = sumInProgress;
      this.sumSuccess = sumSuccess;
      this.sumFail = sumFail;
      this.sumIgnored = sumIgnored;
      this.devicesStatus = devicesStatus;
      this.nextToken = nextToken;
      this.statusFilter = statusFilter;
      // filtering results
      const filteredDevices = devices.filter(
        (device) =>
          this.statusFilter === 'ALL' ||
          device?.jobExecutionSummary?.status === this.statusFilter,
      );
      await this.setDevices(filteredDevices);
      // preparing filter
      for (const status of devicesStatus) {
        this.statusFilters.push(status);
      }

      this.successProgress = Math.round((100 * this.sumSuccess) / this.sumAll);
      this.failProgress = Math.round((100 * this.sumFail) / this.sumAll);
      this.ignoredProgress = Math.round((100 * this.sumIgnored) / this.sumAll);

      this.group$ = this.jobService.getGroupForJob(this.jobId).pipe(
        catchError((err) => {
          this.notif.showError(
            `Error fetching group information : ${err.message ?? err}`,
            err,
          );
          return of(null);
        }),
      );

      // estimate the number of devices not yet queued
      const remainingDevicesToQueue = this.estimateNotYetQueuedCount();
      if (remainingDevicesToQueue === null) {
        this.remainingDevicesToQueue = null;
      } else {
        // subtract the number of devices already handled (queued, done, failed ...)
        const minusHandled = remainingDevicesToQueue - this.sumAll;
        // keep a positive number in case of wrong estimation
        this.remainingDevicesToQueue = minusHandled >= 0 ? minusHandled : 0;
      }
    } catch (e) {
      this.notif.showError((e as Error).message, e);
    }
  }

  /**
   * Estimates the number of devices that are not yet queued for the job.
   * Depending on the job type and configurations, it may
   * - return the sum the filter counts (targeted versions)
   * - return the number of things in the Group ("Group of Things")
   * - return the sum of devices in ThingGroups
   * @private
   */
  estimateNotYetQueuedCount(): number | null {
    if (!this.metaversionJob) return null; // no deployment

    if (this.metaversionJob.estimatedDeviceCount) {
      return this.metaversionJob.estimatedDeviceCount;
    } else if (this.metaversionJob.targetedVersions?.length) {
      return this.metaversionJob.targetedVersions.reduce(
        (acc, filter) => acc + filter.count,
        0,
      );
    }

    return null;
  }

  private isJobExecutionStatusAll(
    value?: unknown,
  ): value is JobExecutionStatusAll {
    const jobExecutionStatusAllValues = [
      'ALL',
      'CANCELED',
      'FAILED',
      'IN_PROGRESS',
      'QUEUED',
      'REJECTED',
      'REMOVED',
      'SUCCEEDED',
      'TIMED_OUT',
    ];

    return (
      !!value &&
      typeof value === 'string' &&
      jobExecutionStatusAllValues.includes(value)
    );
  }

  /**
   * Sets the devices and then retrieves their "nickname"
   * @param devices
   */
  async setDevices(devices: DeploymentThing[]): Promise<void> {
    this.devices = devices; // sets the devices first

    const serialToDeviceReference = devices.reduce(
      (obj, _device) => {
        if (_device.thingArn) {
          const serial = ThingUtils.removeThingType(
            this.splitSerial(_device.thingArn),
          );
          obj[serial] = _device;
        }
        return obj;
      },
      {} as { [serial: string]: DeploymentThing },
    );

    const serials = Object.keys(serialToDeviceReference);

    let simpleThingProperties: SimpleThingProperties[] = [];

    // chunked requests to avoid saturating the request body (~8kB)
    for (const _serials of Utils.splitArrayToFixedSizeChunks(serials, 200)) {
      const properties = await lastValueFrom(
        this.thingsService.getThingProperties(_serials).pipe(
          catchError((e) => {
            console.error('getThingProperties failed', e);
            return [];
          }),
        ),
      );
      simpleThingProperties = [...simpleThingProperties, ...properties];
    }

    // adds "nickname" to devices with a nickname
    simpleThingProperties.forEach(({ serialnumber, nickname }) => {
      const deviceReference = serialToDeviceReference[serialnumber];
      if (deviceReference) {
        deviceReference.nickname = nickname;
      }
    });
  }

  computeEstimatedTime(): string {
    const maximumPerMinute =
      this.metaversionJob?.progressiveRollout?.progressiveRolloutConfiguration
        ?.maximumPerMinute;
    if (!maximumPerMinute) {
      return '';
    }

    return RolloutUtils.computeEstimatedTime(this.sumAll, maximumPerMinute);
  }
}
